import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MaterialReactTable } from "material-react-table";
import { Box, Button, Chip, IconButton, TextField, MenuItem } from "@mui/material";
import { Download, Edit, Visibility, Add, DeleteOutline } from "@mui/icons-material";
import toast from "react-hot-toast";

import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import MDButton from "../../components/MDButton";
import apiService from "../../services/api-service";

const VerbaliList = () => {
	const navigate = useNavigate();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [globalFilter, setGlobalFilter] = useState("");

	// Filtri
	const [filters, setFilters] = useState({
		cantiere_id: "",
		stato: "",
		tipologia_id: "",
	});

	// Opzioni filtri
	const [tipologie, setTipologie] = useState([]);

	const currentAreaId = localStorage.getItem("areaSelected");

	useEffect(() => {
		fetchVerbali();
		fetchTipologie();
	}, [filters]);

	const fetchVerbali = async () => {
		setLoading(true);
		try {
			const params = {
				...filters,
				search: globalFilter,
			};

			// Rimuovi parametri vuoti
			Object.keys(params).forEach((key) => {
				if (!params[key]) delete params[key];
			});

			const response = await apiService.get("/meetings/verbali/", params, {});
			setData(response || []);
		} catch (error) {
			toast.error("Errore caricamento verbali");
			console.error(error);
		} finally {
			setLoading(false);
		}
	};

	const fetchTipologie = async () => {
		try {
			const response = await apiService.get("/meetings/tipologie/", {}, {});
			setTipologie(response || []);
		} catch (error) {
			console.error("Errore caricamento tipologie:", error);
		}
	};

	const handleExportPDF = async (id, numero) => {
		try {
			toast.loading("Generazione PDF...", { id: "pdf-download" });

			const response = await apiService.get(`/meetings/verbali/${id}/export-pdf/`, {}, {}, { responseType: "blob" });

			// Crea blob e download
			const url = window.URL.createObjectURL(new Blob([response]));
			const link = document.createElement("a");
			link.href = url;
			link.download = `verbale_${numero}.pdf`;
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			toast.success("PDF scaricato", { id: "pdf-download" });
		} catch (error) {
			toast.error("Errore download PDF", { id: "pdf-download" });
			console.error(error);
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm("Sei sicuro di voler eliminare questo verbale?")) {
			return;
		}

		try {
			await apiService.delete(`/meetings/verbali/${id}/`, {}, {});
			toast.success("Verbale eliminato");
			fetchVerbali();
		} catch (error) {
			toast.error("Errore eliminazione verbale");
			console.error(error);
		}
	};

	const columns = useMemo(
		() => [
			{
				accessorKey: "numero",
				header: "N° Verbale",
				size: 150,
			},
			{
				accessorKey: "data_riunione",
				header: "Data",
				Cell: ({ cell }) =>
					new Date(cell.getValue()).toLocaleString("it-IT", {
						day: "2-digit",
						month: "2-digit",
						year: "numeric",
					}),
				size: 150,
			},
			{
				accessorKey: "cantiere_nome",
				header: "Cantiere",
				size: 200,
			},
			{
				accessorKey: "tipologia_nome",
				header: "Tipologia",
				size: 150,
			},
			{
				accessorKey: "luogo",
				header: "Luogo",
				size: 200,
			},
			{
				accessorKey: "num_firme",
				header: "Firme",
				Cell: ({ row }) => (
					<span>
						{row.original.num_firme} / {row.original.num_partecipanti}
					</span>
				),
				size: 100,
			},
			{
				accessorKey: "stato",
				header: "Stato",
				Cell: ({ cell }) => {
					const colors = {
						BOZZA: "default",
						IN_FIRMA: "warning",
						COMPLETATO: "success",
					};
					return <Chip label={cell.getValue()} color={colors[cell.getValue()]} size="small" />;
				},
				size: 120,
			},
		],
		[]
	);

	return (
		<DashboardLayout>
			<DashboardNavbar />
			<Box sx={{ p: 3 }}>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
					<h2>Verbali di Riunione</h2>
					<MDButton variant="contained" color="info" startIcon={<Add />} onClick={() => navigate("/meetings/verbali/new")}>
						Nuovo Verbale
					</MDButton>
				</Box>

				{/* Filtri */}
				<Box sx={{ gap: 2, mb: 3, display: "flex", alignItems: "center" }}>
					<TextField
						label="Ricerca"
						value={globalFilter}
						onChange={(e) => setGlobalFilter(e.target.value)}
						placeholder="Cerca per numero, luogo, ODG..."
						size="small"
						sx={{ minWidth: 300 }}
					/>

					<TextField
						select
						label="Tipologia"
						value={filters.tipologia_id}
						onChange={(e) => setFilters({ ...filters, tipologia_id: e.target.value })}
						size="small"
						sx={{ minWidth: 200 }}
						SelectProps={{
							displayEmpty: true,
						}}
						InputLabelProps={{
							shrink: true,
						}}
					>
						<MenuItem value="">Tutte</MenuItem>
						{tipologie.map((t) => (
							<MenuItem key={t.id} value={t.id}>
								{t.nome}
							</MenuItem>
						))}
					</TextField>

					<TextField
						select
						label="Stato"
						value={filters.stato}
						onChange={(e) => setFilters({ ...filters, stato: e.target.value })}
						size="small"
						sx={{ minWidth: 150 }}
						SelectProps={{
							displayEmpty: true,
						}}
						InputLabelProps={{
							shrink: true,
						}}
					>
						<MenuItem value="">Tutti</MenuItem>
						<MenuItem value="BOZZA">Bozza</MenuItem>
						<MenuItem value="IN_FIRMA">In Firma</MenuItem>
						<MenuItem value="COMPLETATO">Completato</MenuItem>
					</TextField>

					<Button variant="outlined" onClick={fetchVerbali}>
						Cerca
					</Button>
				</Box>

				{/* Tabella */}
				<MaterialReactTable
					columns={columns}
					data={data}
					state={{
						isLoading: loading,
					}}
					enableRowActions
					renderRowActions={({ row }) => (
						<Box sx={{ display: "flex", gap: 1 }}>
							<IconButton onClick={() => navigate(`/meetings/verbali/${row.original.id}`)} title="Visualizza">
								<Visibility />
							</IconButton>

							{row.original.stato === "BOZZA" && (
								<IconButton
									onClick={() => navigate(`/meetings/verbali/${row.original.id}/edit`)}
									title="Modifica"
									color="info"
								>
									<Edit />
								</IconButton>
							)}

							<IconButton
								onClick={() => handleExportPDF(row.original.id, row.original.numero)}
								title="Download PDF"
								color="success"
							>
								<Download />
							</IconButton>

							{row.original.stato === "BOZZA" && (
								<IconButton onClick={() => handleDelete(row.original.id)} title="Elimina" color="error">
									<DeleteOutline />
								</IconButton>
							)}
						</Box>
					)}
					enableSorting
					enableColumnFilters={false}
					enablePagination
					initialState={{
						pagination: { pageSize: 10, pageIndex: 0 },
						sorting: [{ id: "data_riunione", desc: true }],
					}}
					muiTablePaperProps={{
						elevation: 0,
						sx: {
							border: "1px solid #e0e0e0",
						},
					}}
				/>
			</Box>
		</DashboardLayout>
	);
};

export default VerbaliList;
